/**
 * Spinner.tsx
 *
 * Reusable loading spinner component used across all pages and protected routes.
 * Replaces the previously duplicated inline spinner markup.
 *
 * Usage:
 *   <Spinner />               — default medium size, centered in a 64-unit-tall block
 *   <Spinner size="lg" />     — large variant used by ProtectedRoute (full-screen)
 *   <Spinner size="sm" />     — small variant used inside buttons (e.g. Save/Saving…)
 *
 * Sizes map to Tailwind width/height classes:
 *   sm  → w-5 h-5
 *   md  → w-8 h-8  (default)
 *   lg  → w-10 h-10
 */

import React from 'react';

interface SpinnerProps {
  /** Controls the diameter of the spinner. Defaults to "md". */
  size?: 'sm' | 'md' | 'lg';
  /** Extra Tailwind classes forwarded to the outer wrapper div. */
  className?: string;
}

const sizeClasses: Record<NonNullable<SpinnerProps['size']>, string> = {
  sm: 'w-5 h-5',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
};

/**
 * Centered loading spinner with the brand teal colour.
 * Wraps the spinning ring in a flex container so it centres itself
 * within whatever parent it is placed in.
 */
export default function Spinner({ size = 'md', className = '' }: SpinnerProps) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div
        className={`${sizeClasses[size]} border-4 border-teal-500 border-t-transparent rounded-full animate-spin`}
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}
